import {Pref} from "../../../../generated/graphql";
import {TenantPreferences} from "../../../../constants/constants";
import {extractSimplePref} from "../../../../providers/PreferenceProvider";
import {DriverRequirementEnforcementLevel} from "../../enums/DriverRequirementEnforcementLevel";

// DP-3166: companies that never saved "Consider Skills in Itinerary Planning" get "Yes".
export const DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING = true;
// DP-3230: "Yes" (and an unsaved company) blocks moving a job onto a driver who is missing a skill on the IP map.
export const DEFAULT_MAP_SKILL_ENFORCEMENT = DriverRequirementEnforcementLevel.Prevent;

export type MapSkillEnforcement = DriverRequirementEnforcementLevel.Prevent | DriverRequirementEnforcementLevel.Warning;

// Radio values of the setting. "true"/"false" keep the test ids the two-option setting had.
export enum ConsiderSkillsOption {
  Yes = "true",
  YesAllowWithConfirmation = "Warning",
  No = "false"
}

// Anything but Warning blocks, so an unexpected stored value never weakens the setting.
export const toMapSkillEnforcement = (value: unknown): MapSkillEnforcement =>
  value === DriverRequirementEnforcementLevel.Warning
    ? DriverRequirementEnforcementLevel.Warning
    : DriverRequirementEnforcementLevel.Prevent;

export const toConsiderSkillsOption = (
  considerSkills: boolean,
  mapEnforcement: MapSkillEnforcement
): ConsiderSkillsOption => {
  if (!considerSkills) return ConsiderSkillsOption.No;
  return mapEnforcement === DriverRequirementEnforcementLevel.Warning
    ? ConsiderSkillsOption.YesAllowWithConfirmation
    : ConsiderSkillsOption.Yes;
};

export type ItinerarySkillsSettings = {
  // Sent to the routing engine.
  considerSkills: boolean;
  // How the IP map treats a job moved onto a driver who is missing a skill; null = no check.
  mapEnforcement: MapSkillEnforcement | null;
};

export const resolveItinerarySkills = (
  tenantPreferences: Pref[],
  itineraryPlanningSkillsEnabled: boolean
): ItinerarySkillsSettings => {
  const considerSkills =
    itineraryPlanningSkillsEnabled &&
    (extractSimplePref(
      tenantPreferences,
      TenantPreferences.considerSkillsRoutePlanning,
      DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING
    ).value as boolean);

  if (!considerSkills) return {considerSkills: false, mapEnforcement: null};

  const storedMapEnforcement = extractSimplePref(
    tenantPreferences,
    TenantPreferences.mapSkillEnforcementRoutePlanning,
    DEFAULT_MAP_SKILL_ENFORCEMENT
  ).value;
  return {considerSkills: true, mapEnforcement: toMapSkillEnforcement(storedMapEnforcement)};
};
